import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  preloadAuthRoutes,
  preloadDashboardRoutes,
  preloadCriticalRoutes,
} from '@/routes/lazyRoutes';

interface RoutePreloaderProps {
  isAuthenticated: boolean
}

export const RoutePreloader: React.FC<RoutePreloaderProps> = ({ isAuthenticated }) => {
  const location = useLocation();

  // Preload critical routes on mount
  useEffect(() => {
    preloadCriticalRoutes();
  }, []);

  // Preload routes based on authentication status
  useEffect(() => {
    if (isAuthenticated) {
      preloadDashboardRoutes();
    } else {
      preloadAuthRoutes();
    }
  }, [isAuthenticated]);

  // Preload routes based on current location
  useEffect(() => {
    const currentPath = location.pathname;

    // Preload related routes based on current path
    if (currentPath.startsWith('/auth')) {
      preloadAuthRoutes();
    } else if (currentPath.startsWith('/dashboard')) {
      preloadDashboardRoutes();
    }
  }, [location.pathname]);

  // Preload on hover for better UX
  const handleLinkHover = useCallback((routeType: 'auth' | 'dashboard') => {
    if (routeType === 'auth') {
      preloadAuthRoutes();
    } else if (routeType === 'dashboard') {
      preloadDashboardRoutes();
    }
  }, []);

  // Expose preload methods for external use
  useEffect(() => {
    // Add to window for external access if needed
    (window as any).preloadRoutes = {
      auth: preloadAuthRoutes,
      dashboard: preloadDashboardRoutes,
    };
  }, []);

  return null; // This component doesn't render anything
};
